import React, { useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@frontend/shadcn/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@frontend/shadcn/components/ui/alert-dialog';
import { Tag } from '@frontend/app/refresh-components';
import { ProjectsRouteRoot } from '@frontend/app/containers/Projects/constants';
import { AUTOMATION_EXECUTION_TERMINABLE_STATUSES, AUTOMATION_EXECUTION_ENDED_STATUSES } from '@frontend/app/components/AutomationAuditTable/AutomationAuditTableTypes';
import { AutomationExecutionStatus } from '@services/automations/types/AutomationExecution';
import { ListExecutions_listExecutions_results, ListExecutions_listExecutions_results_variables } from '@frontend/app/queries/types/ListExecutions';
import { useMessagingContext } from '@/hooks';
import { useListExecutions, useTerminateAutomationExecutionMutation } from '@/app/hooks/automations';
import { useGetMembersByIdsQuery } from '@/app/hooks';
import { GetMemberQuery_member } from '@/app/queries/types/GetMemberQuery';
import { AutomationVariableType } from '@/app/types/globalTypes';
import { formatDateToReadableString, getAutomationExecutionStatus } from '../utils/automationUtils';
import { AutomationExecution } from '../constants';

// Constants
const TABLE_PAGE_SIZE = 200;

interface PagingInfo {
  totalCount: number;
  offset: number;
  limit: number;
  count: number;
  hasMore: boolean;
}

type TableColumnDef = ColumnDef<AutomationExecution> & {
  accessorKey?: string;
  meta?: {
    label: string;
  };
};

// Utils
function getMemberIdFromAutomationExecution(execution: ListExecutions_listExecutions_results): number | undefined {
  const member = execution?.variables?.find((variable: ListExecutions_listExecutions_results_variables) => variable.type === AutomationVariableType.MEMBER);
  return member ? parseInt(member.value, 10) : undefined;
}

function listExecutionToAutomationExecutionAdapter(
  execution: ListExecutions_listExecutions_results,
  member?: GetMemberQuery_member,
): AutomationExecution {
  return {
    automation: execution?.automation,
    member,
    status: execution.status,
    dateTriggered: execution.createdDate,
    dateEnded: AUTOMATION_EXECUTION_ENDED_STATUSES.includes(execution.status) ? execution.updatedDate : null,
    id: execution.id,
  };
}

type TProps = {
  projectId: number;
};

// Add CancelDialog component before the main component
interface CancelDialogProps {
  execution: AutomationExecution;
  onTerminate: (executionId: string) => Promise<void>;
  onSuccess: () => void;
}

function CancelDialog({ execution, onTerminate, onSuccess }: CancelDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleTerminate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onTerminate(execution.id);
      onSuccess();
      setIsOpen(false);
    } catch (error) {
      // Error handling is done in the parent component
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="destructive">
          Cancel
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure you want to cancel this automation run?</AlertDialogTitle>
          <AlertDialogDescription>
            {`${execution.member?.name} will be removed from the ${execution.automation.name} automation and the action will not be performed.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <form onSubmit={handleTerminate}>
            <Button type="submit">Continue</Button>
          </form>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AutomationExecutionTable({ projectId }: TProps) {
  // State
  const [pageIndex, setPageIndex] = useState(0);
  const [terminateAutomationExecution] = useTerminateAutomationExecutionMutation();
  const { showSuccessMessage, showErrorMessage } = useMessagingContext();

  // Data fetching
  const {
    loading: isLoading,
    data: { listExecutions: { results: executions = [], paging } } = {
      listExecutions: {
        results: [],
        paging: { totalCount: 0 } as PagingInfo,
      },
    },
    refetch: refetchExecutions,
  } = useListExecutions({
    variables: {
      paging: {
        offset: pageIndex * TABLE_PAGE_SIZE,
        limit: TABLE_PAGE_SIZE,
      },
      filters: {
        automation: {
          program: {
            id: projectId,
          },
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  // Fetch member data for executions
  const memberIds = executions.map(getMemberIdFromAutomationExecution).filter(Boolean) as number[];
  const { data: { members: membersByIds = [] } = {} } = useGetMembersByIdsQuery(memberIds, {
    skip: isLoading || !memberIds.length,
    fetchPolicy: 'no-cache',
  });

  // Adapt executions with member data
  const data = useMemo(
    () =>
      executions.map((execution) => {
        const member = membersByIds.find(
          (member) => member.id === getMemberIdFromAutomationExecution(execution),
        );
        return listExecutionToAutomationExecutionAdapter(execution, member);
      }),
    [executions, membersByIds],
  );

  const handleTerminateExecution = useCallback(async (executionId: string) => {
    try {
      await terminateAutomationExecution({
        variables: {
          executionId,
          reason: 'User Initiated',
        },
      });
      showSuccessMessage('Automation execution terminated successfully');
      refetchExecutions();
    } catch (error) {
      showErrorMessage('Failed to terminate automation execution');
    }
  }, [terminateAutomationExecution, showSuccessMessage, showErrorMessage, refetchExecutions]);

  // Columns
  const columns = useMemo<TableColumnDef[]>(
    () => [
      {
        accessorKey: 'dateTriggered',
        header: 'Date Triggered',
        meta: { label: 'Date Triggered' },
        cell: ({ row }) => formatDateToReadableString(row.getValue('dateTriggered')),
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'dateEnded',
        header: 'Date Ended',
        meta: { label: 'Date Ended' },
        cell: ({ row }) => formatDateToReadableString(row.getValue('dateEnded')),
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'automationName',
        header: 'Automation Name',
        meta: { label: 'Automation Name' },
        cell: ({ row }) => (
          <Button className="text-primary underline group hover:text-primary m-0 p-0" variant="link" asChild>
            <Link to={`${ProjectsRouteRoot}/${projectId}/automation_detail/automation/${row.original.automation.id}`}>
              {row.original.automation.name}
            </Link>
          </Button>
        ),
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'member',
        header: 'Member',
        meta: { label: 'Member' },
        cell: ({ row }) => {
          const name = row.original.member?.name;
          const initials = name
            ?.split(' ')
            .map((n) => n[0]?.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ''))
            .join('');
          if (!row?.original?.member) {
            return 'N/A';
          }
          return (
            <Link className="text-primary group hover:text-primary" to={`/members/${row.original.member?.id}`}>
              <div className="flex items-center gap-2">
                <Avatar className="w-8 h-8">
                  <AvatarImage
                    src={row.original.member?.profilePicture}
                    alt={row.original.member?.name}
                    className="object-cover"
                  />
                  <AvatarFallback className="text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className="text-sm font-medium underline group-hover:underline">{row.original.member?.name}</div>
              </div>
            </Link>
          );
        },
        size: 200,
        minSize: 200,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        meta: { label: 'Status' },
        cell: ({ row }) => (
          <Tag
            label={getAutomationExecutionStatus(row.original.status)?.text}
            bgColor={getAutomationExecutionStatus(row.original.status)?.bg}
            textColor="text-grey-6"
          />
        ),
        size: 120,
        minSize: 120,
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        meta: { label: 'Actions' },
        cell: ({ row }) => {
          return AUTOMATION_EXECUTION_TERMINABLE_STATUSES.includes(row.original.status as AutomationExecutionStatus) ? (
            <CancelDialog
              execution={row.original}
              onTerminate={handleTerminateExecution}
              onSuccess={() => refetchExecutions()}
            />
          ) : null;
        },
        size: 100,
        minSize: 100,
      },
    ],
    [projectId, handleTerminateExecution, refetchExecutions],
  );

  // Table component
  const tableComponent = useMemo(
    () => (
      <DataTable
        columns={columns}
        data={data}
        isLoading={isLoading}
        enableColumnResizing
        className="h-full"
      />
    ),
    [columns, data, isLoading],
  );

  // Calculate pagination range and next page logic using paging object
  const offset = paging?.offset ?? 0;
  const limit = paging?.limit ?? TABLE_PAGE_SIZE;
  const count = paging?.count ?? executions.length;
  const hasMore = paging?.hasMore ?? false;

  // Render
  return (
    <div className="w-full border-grey-2 rounded-2xl overflow-hidden border-solid border-[1px] flex flex-col">
      <div className="w-full bg-white flex justify-between items-center px-4 py-2 border-b border-grey-2">
        <div className="text-sm text-muted-foreground">
          {executions?.length} Events
        </div>
        <TablePagination
          pageIndex={Math.floor(offset / limit)}
          pageSize={limit}
          totalCount={count}
          canPreviousPage={offset > 0}
          canNextPage={hasMore}
          onPaginationChange={(newPageIndex) => setPageIndex(newPageIndex)}
        />
      </div>
      <div className="flex-1 overflow-hidden">{tableComponent}</div>
    </div>
  );
}
